import type { ReactNode } from 'react';

import { RouteWrapper } from '@/views/CrehanaLegal/components';

import type { TBlock, TSection } from './sla-content';
import { SECTIONS } from './sla-content';
import { Table } from './Table';

// biome-ignore lint/complexity/noBannedTypes: legacy code - complexity/noBannedTypes
type TSLAProps = {};

type TSubSectionTitleProps = { children: ReactNode };

const SubSectionTitle = ({ children }: TSubSectionTitleProps) => (
  <h4 className="font-body3 font-semibold mb-8">{children}</h4>
);

/** Brand-blue top-level section heading — local to the SLA route only. */
type TSectionTitleProps = { children: ReactNode };

const SectionTitle = ({ children }: TSectionTitleProps) => (
  <h3 className="font-subtitle3 mb-16 text-primary-main">{children}</h3>
);

type TTableOfContentsProps = { sections: TSection[] };

const TableOfContents = ({ sections }: TTableOfContentsProps) => (
  <section>
    <SectionTitle>Índice</SectionTitle>
    <nav>
      <ul>
        {sections.map(section => (
          <li key={section.id}>
            <a href={`#${section.id}`}>{section.tocLabel}</a>
          </li>
        ))}
      </ul>
    </nav>
  </section>
);

const renderBlock = (block: TBlock, index: number): ReactNode => {
  if (block.kind === 'p') {
    return <p key={index}>{block.text}</p>;
  }

  if (block.kind === 'note') {
    return (
      <p key={index} className="font-italic">
        <em>{block.text}</em>
      </p>
    );
  }

  if (block.kind === 'list') {
    return (
      <ul key={index}>
        {block.items.map((item, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static content list, index is stable
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.kind === 'table') {
    return (
      <Table
        key={index}
        columns={block.columns}
        rows={block.rows}
        hideHeader={block.hideHeader}
        boldFirstColumn={block.boldFirstColumn}
      />
    );
  }

  if (block.kind === 'subsection') {
    return (
      <div key={index}>
        <SubSectionTitle>{block.title}</SubSectionTitle>
        {block.blocks.map((subBlock, subIndex) =>
          renderBlock(subBlock, subIndex),
        )}
      </div>
    );
  }

  return null;
};

const CONTROL_DE_VERSIONES_COLUMNS = [
  { field: 'version', headerName: 'Versión' },
  { field: 'fecha', headerName: 'Fecha' },
  { field: 'descripcion', headerName: 'Descripción del cambio' },
  { field: 'aprobado', headerName: 'Aprobado por' },
];

const CONTROL_DE_VERSIONES_ROWS = [
  {
    id: 1,
    version: '1.0',
    fecha: '18/06/2026',
    descripcion: 'Emisión inicial del Master ANS',
    aprobado: 'Legal / Operaciones',
  },
];

export const SLA: React.FC<React.PropsWithChildren<TSLAProps>> = () => {
  return (
    <RouteWrapper>
      {/* Control de Versiones (AC11) */}
      <section>
        <SectionTitle>Control de Versiones</SectionTitle>
        <Table
          columns={CONTROL_DE_VERSIONES_COLUMNS}
          rows={CONTROL_DE_VERSIONES_ROWS}
        />
      </section>

      <TableOfContents sections={SECTIONS} />
      {SECTIONS.map(section => (
        <section key={section.id} id={section.id}>
          <SectionTitle>{section.heading}</SectionTitle>
          {section.blocks.map((block, index) => renderBlock(block, index))}
        </section>
      ))}
    </RouteWrapper>
  );
};

export default SLA;
